import React, { useRef, useEffect, useState } from 'react';
import './RightPanelEditor.scss';

export interface RightPanelEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

const RightPanelEditor: React.FC<RightPanelEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter your code here...',
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  // Function to apply simple Markdown highlighting
  const highlightMarkdown = (text: string): string => {
    if (!text) return '';

    // Replace Markdown syntax with HTML with appropriate classes
    let highlighted = text
      // Headers
      .replace(/^(#{1,6})\s(.+)$/gm, '<span class="md-header">$1 $2</span>')
      // Bold - process bold first to avoid conflicts with italic
      .replace(/\*\*([^*]+?)\*\*/g, '<span class="md-bold">**$1**</span>')
      // Italic - only match single asterisks that aren't part of bold syntax
      .replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<span class="md-italic">*$1*</span>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<span class="md-codeblock">```$1```</span>')
      // Inline code
      .replace(/`([^`]+)`/g, '<span class="md-code">`$1`</span>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<span class="md-link">[$1]($2)</span>')
      // Lists
      .replace(/^(\s*[-*+])\s(.+)$/gm, '<span class="md-list">$1 $2</span>')
      // Quotes
      .replace(/^(\s*>)\s(.+)$/gm, '<span class="md-quote">$1 $2</span>')
      // Variables with double curly braces {{variable}} - modified to handle the entire pattern
      .replace(/(\{\{)([^}]+)(\}\})/g, '<span class="md-variable">{{$2}}</span>');

    // Replace newlines with <br> to maintain formatting
    highlighted = highlighted.replace(/\n/g, '<br>');

    return highlighted;
  };

  // Sync scroll between textarea and highlight div
  const handleScroll = () => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Update cursor position for better UX
  const handleSelection = () => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  };

  // Sync textarea and highlight div on content change
  useEffect(() => {
    handleScroll();
  }, [value]);

  return (
    <div className="right-panel-editor">
      <div
        className="right-panel-editor__highlight"
        ref={highlightRef}
        dangerouslySetInnerHTML={{ __html: highlightMarkdown(value) }}
      />
      <textarea
        ref={textareaRef}
        className="right-panel-editor__textarea"
        value={value}
        onChange={e => onChange && onChange(e.target.value)}
        onScroll={handleScroll}
        onClick={handleSelection}
        onKeyUp={handleSelection}
        placeholder={placeholder}
        spellCheck={false}
      />
    </div>
  );
};

export default RightPanelEditor;
